import { KButton, KDrawer, KMenuButton } from '@/ui-library/components';
import {
  DrawerFooter,
  DrawerHeader,
  Flex,
  Box,
  Button,
  Icon,
  Spinner,
  MenuItem,
} from '@chakra-ui/react';
import { Mail02 } from '@kula-ai/icons-core';
import { Docusign, Pandadoc } from '@/assets/icons/esign';
import { Adobesign } from '@/assets/icons/ats';
import { KText } from '@/ui-library/components/text';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { getVariantStyles } from '@/ui-library/components/molecules/KDrawer/KDrawer.styles';
import { KSteps } from '@/ui-library/components/molecules/KSteps';
import {
  OFFER_STEPS_APPROVAL,
  OFFER_STEPS_REAPPROVAL_EDIT,
  OFFER_STEPS_SEND,
  OFFER_STEPS_SEND_DOCUSIGN,
  OFFER_STEPS_SEND_PANDADOC,
  OFFER_STEPS_SEND_ADOBE,
  OFFER_STEPS_SEND_SPOTDRAFT,
  OFFER_STEPS_ESIGN_DOCUSIGN,
  OFFER_STEPS_ESIGN_PANDADOC,
  OFFER_STEPS_ESIGN_ADOBE,
  OFFER_STEPS_ESIGN_SPOTDRAFT,
  STEP_STATUS_COMPLETED,
  STEP_STATUS_ACTIVE,
  STEP_STATUS_PENDING,
  getOfferSteps,
} from './constants';
import { cloneDeep } from 'lodash-es';
import {
  shouldSkipReapproval,
  shouldShowSkipDocButton,
  offerNeedsApprovalBeforeSend,
  isDraftLikeOffer,
  connectedSignatureIds,
} from './helpers';
import { OfferBodyContainer } from './OfferBodyContainer';
import { AddMoreFilesButton } from './components/offer-template/AddMoreFilesButton';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import { useOfferStore } from './store';
import {
  offerDetailsAPI,
  useGetApprovalSettingsQuery,
  useGetCustomFieldsQuery,
  useSendWithSpotDraftMutation,
} from './api';
import {
  useSendWithDocuSignMutation,
  useSendWithPandaDocMutation,
  useSendWithAdobeMutation,
} from '@/store/query/offer-Initiation';
import { useDocusignPreviewFlow } from '@/components/ats/OfferInitiation/useDocusignPreviewFlow';
import {
  generateOfferPayload,
  shouldCreateOffer,
  getInitializeOfferValues,
  getMissingRequiredCustomFields,
} from '@/components/ats/OfferInitiation/offerFormHelpers';
import { processFormFilesForUpload } from '@/utils/fileUploadHelpers';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { getErrorMessage } from '@/utils/errors';

const SIGNATURE_OPTIONS = {
  kula: { label: 'Send with Kula', icon: Mail02 },
  docusign: { label: 'Send with DocuSign', icon: Docusign },
  pandadoc: { label: 'Send with PandaDoc', icon: Pandadoc },
  adobe: { label: 'Send with Adobe Sign', icon: Adobesign },
};

const getInitialSteps = (
  sendMode: boolean,
  eSignType?: 'docusign' | 'pandadoc' | 'adobe' | 'spotdraft',
  reapprovalEdit?: boolean
) => {
  // Skip-flow re-approval edit: Details → Approval only (doc already generated).
  if (reapprovalEdit) return OFFER_STEPS_REAPPROVAL_EDIT;
  if (eSignType === 'docusign')
    return sendMode ? OFFER_STEPS_SEND_DOCUSIGN : OFFER_STEPS_ESIGN_DOCUSIGN;
  if (eSignType === 'pandadoc')
    return sendMode ? OFFER_STEPS_SEND_PANDADOC : OFFER_STEPS_ESIGN_PANDADOC;
  if (eSignType === 'adobe')
    return sendMode ? OFFER_STEPS_SEND_ADOBE : OFFER_STEPS_ESIGN_ADOBE;
  if (eSignType === 'spotdraft')
    return sendMode ? OFFER_STEPS_SEND_SPOTDRAFT : OFFER_STEPS_ESIGN_SPOTDRAFT;
  return sendMode ? OFFER_STEPS_SEND : OFFER_STEPS_APPROVAL;
};

const getHeaderTitle = (
  type: 'create' | 'edit',
  sendMode: boolean,
  eSignType?: 'docusign' | 'pandadoc' | 'adobe' | 'spotdraft',
  candidateName?: string
) => {
  const suffix = candidateName ? ` for ${candidateName}` : '';
  if (eSignType === 'docusign') return `Send offer via Docusign${suffix}`;
  if (eSignType === 'pandadoc') return `Send offer via Pandadoc${suffix}`;
  if (eSignType === 'adobe') return `Send offer via Adobe Sign${suffix}`;
  if (eSignType === 'spotdraft') return `Send offer via SpotDraft${suffix}`;
  if (sendMode) return `Send offer via Kula${suffix}`;
  return `${type === 'edit' ? 'Edit' : 'Create'} offer${suffix}`;
};

interface OfferContainerProps {
  onClose: () => void;
  applicationData: any;
  inComingOffer?: {
    id: number;
    status: string;
  };
  // For the SpotDraft send-mode path, initiateAPIContract awaits this before
  // clearing isCreationInProgress — a caller can return a promise (e.g. one
  // that awaits polling) to keep the primary button loading past the initial
  // send.
  onSuccess: () => void | Promise<void>;
  type: 'create' | 'edit';
  contact?: any;
  redirectToSendForApprovalTab?: boolean;
  sendMode?: boolean;
  eSignType?: 'docusign' | 'pandadoc' | 'adobe' | 'spotdraft';
  // Skip flow: called from the template step when a missing re-approval field was
  // filled inline, to hand off to the full edit drawer (Details + Approval).
  onEditOffer?: (_tokens?: string[]) => void;
  // Token names of the missing re-approval fields to flag on the Details step.
  reapprovalHighlightTokens?: string[];
}

const OfferContainer = ({
  inComingOffer = null,
  onClose,
  applicationData,
  onSuccess,
  contact,
  type = 'create',
  redirectToSendForApprovalTab = false,
  sendMode = false,
  eSignType,
  onEditOffer,
  reapprovalHighlightTokens = [],
}: OfferContainerProps) => {
  const { data: approvalSettings, isFetching: getApprovalInProgress } =
    useGetApprovalSettingsQuery();
  const { data: customFieldsData } = useGetCustomFieldsQuery(
    { subject_type: 'ats_offer' },
    { enabled: true }
  );
  // Scoped fields the body renders (via store); fall back to unscoped before it mounts.
  const scopedFormFields = useOfferStore((state) => state.scopedFormFields);
  const mapSourceFields = useMemo(
    () =>
      scopedFormFields?.length
        ? scopedFormFields
        : customFieldsData?.ats_form_fields || [],
    [scopedFormFields, customFieldsData]
  );
  const cfIdToRowIdMap = useMemo(
    () =>
      (mapSourceFields || []).reduce(
        (acc: Record<string, string>, f: any) => {
          if (
            f?.ats_custom_field_id !== null &&
            f?.ats_custom_field_id !== undefined &&
            f?.id !== null &&
            f?.id !== undefined
          ) {
            acc[String(f.ats_custom_field_id)] = String(f.id);
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    [mapSourceFields]
  );
  // Mirror the map on a ref so memoized closures read the latest value — a stale empty map
  // ships raw cf_ids the BE can't match (CORE-4798).
  const cfIdToRowIdMapRef = useRef(cfIdToRowIdMap);
  cfIdToRowIdMapRef.current = cfIdToRowIdMap;
  // Memoized for stable identity — it's a dep of initiateOfferInitialization's useCallback.
  const rowIdToCfIdMap = useMemo(
    () =>
      (mapSourceFields || []).reduce(
        (acc: Record<string, string>, f: any) => {
          if (
            f?.ats_custom_field_id !== null &&
            f?.ats_custom_field_id !== undefined &&
            f?.id !== null &&
            f?.id !== undefined
          ) {
            acc[String(f.id)] = String(f.ats_custom_field_id);
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    [mapSourceFields]
  );

  // E-signature mutations
  const [sendWithDocuSign] = useSendWithDocuSignMutation();
  const { runPreview, isRunning: isPreviewRunning } = useDocusignPreviewFlow();
  const [sendWithPandaDoc] = useSendWithPandaDocMutation();
  const { feDocusignOfferPreview, tOfferTemplateMultiDocuments } = useKFlags();
  const [sendWithAdobe] = useSendWithAdobeMutation();
  const sendWithSpotDraftMutation = useSendWithSpotDraftMutation();

  // Opened from the skip-flow "Edit offer" on a re-approval field → 2-step
  // Details → Approval flow (no template step, no skip button).
  const isReapprovalEdit = reapprovalHighlightTokens.length > 0;
  const offerSteps = cloneDeep(
    getInitialSteps(sendMode, eSignType, isReapprovalEdit)
  );
  const [stepItems, setStepItems] = useState(offerSteps) as Array<any>;

  // Use selectors to subscribe to specific store values
  const offerId = useOfferStore((state) => state.offerId);
  const isMultiDocTemplate = useOfferStore((state) => state.isMultiDocTemplate);
  const setOfferId = useOfferStore((state) => state.setOfferId);
  const enableNextButton = useOfferStore((state) => state.enableNextButton);
  const setApplicationData = useOfferStore((state) => state.setApplicationData);
  const signatureType = useOfferStore((state) => state.signatureType);
  const setSignatureType = useOfferStore((state) => state.setSignatureType);
  const setIsApprovalEnabled = useOfferStore(
    (state) => state.setIsApprovalEnabled
  );
  const setIsReapprovalFieldsModified = useOfferStore(
    (state) => state.setIsReapprovalFieldsModified
  );
  const isReapprovalFieldsModified = useOfferStore(
    (state) => state.isReapprovalFieldsModified
  );
  const isApprovalEnabled = useOfferStore((state) => state.isApprovalEnabled);
  const setSkipApproval = useOfferStore((state) => state.setSkipApproval);
  const setCurrentOffer = useOfferStore((state) => state.setCurrentOffer);
  const setOfferAttachments = useOfferStore(
    (state) => state.setOfferAttachments
  );
  const currentOffer = useOfferStore((state) => state.currentOffer);
  const hasValidTokens = useOfferStore((state) => state.hasValidTokens);
  const [createOrUpdateInitiated, setCreateOrUpdateInitiated] = useState(false);
  const { tOfferSkipDocGeneration } = useKFlags();

  const needsApprovalBeforeSend = offerNeedsApprovalBeforeSend({
    isApprovalEnabled,
    offerStatus: currentOffer?.offer_status,
    requiresPreapproval: currentOffer?.requires_preapproval,
  });

  const currentStep = useMemo(() => {
    return stepItems.find((step) => step.status === STEP_STATUS_ACTIVE);
  }, [stepItems]);

  const [isCreationInProgress, setIsCreationInProgress] = useState(false);
  const [isDraftInProgress, setIsDraftInProgress] = useState(false);
  // candidate data
  const getCandidateName = useCallback(() => {
    return contact?.name || '';
  }, [contact?.name]);

  const payload = useRef({
    offerDetails: {},
    offerLetterTemplate: {},
    offerApproval: {},
    offerEmail: {},
  });
  const toast = useShowToast();
  const [isOfferCreated, setIsOfferCreated] = useState(false);

  useEffect(() => {
    if (sendMode || getApprovalInProgress) return;
    const isApprovalEnabled = approvalSettings?.offer_approval_enabled || false;
    setIsApprovalEnabled(isApprovalEnabled);
    // Re-approval edit uses its own fixed 2-step flow; don't overwrite it with
    // the standard offer steps.
    if (isReapprovalEdit) return;
    const updatedSteps = getOfferSteps(
      isApprovalEnabled,
      redirectToSendForApprovalTab
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStepItems(cloneDeep(updatedSteps));
  }, [getApprovalInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const setButtonDisabled = useMemo(() => {
    return (
      currentStep?.id === 'offerLetterTemplate' &&
      (!hasValidTokens || !enableNextButton)
    );
  }, [currentStep?.id, enableNextButton, hasValidTokens]);

  // child refs
  const offerDetailsRef = useRef(null);
  const offerLetterTemplateRef = useRef(null);
  const offerApprovalRef = useRef(null);
  const offerEmailRef = useRef(null);
  const childRefs = useMemo(
    () => ({
      OFFER_DETAILS_COMP: offerDetailsRef,
      OFFER_LETTER_TEMPLATE_COMP: offerLetterTemplateRef,
      OFFER_APPROVAL_COMP: offerApprovalRef,
      OFFER_EMAIL_COMP: offerEmailRef,
    }),
    []
  );

  useEffect(() => {
    // for edit case, we will get offer details
    if (inComingOffer && inComingOffer?.id) {
      setOfferId(inComingOffer?.id);
    } else {
      setOfferId(0);
    }
    // Set application data in store
    setApplicationData(applicationData);
    // Set the signature type for this drawer. Reset to 'kula' when there's no eSignType
    // (the Kula send flow) so a stale value left in the store by a prior e-sign send flow
    // doesn't render the DocuSign email form in the Kula email step.
    setSignatureType((eSignType as any) || 'kula');
    // The offer store is a global singleton that persists across drawer opens; clear per-offer
    // state on close so a prior offer's attachments/document can't leak into the next drawer.
    return () => {
      setOfferAttachments([]);
      setCurrentOffer({});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // business methods
  const validateAndInitiateNextStep = async () => {
    // validate current step and show errors if any
    // else change current step to completed and next step to active
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;

    const doneWithAPIContract = await initiateAPIContract();
    if (!doneWithAPIContract) {
      return;
    }

    const nextStep = currentStep?.nextNode;
    if (!nextStep) {
      return;
    }
    const nextStepItem = stepItems.find((item) => item.id === nextStep);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_COMPLETED };
      }
      if (item.id === nextStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE, enabled: true };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  };

  const initiateKStepSelection = (selectedItem) => {
    const selectedIndex = selectedItem['index'];
    const { status } = selectedItem['step'];
    if (status === STEP_STATUS_ACTIVE) return;
    if (status === STEP_STATUS_PENDING) {
      initiateKStepPendingClick(selectedItem['step']);
      return;
    }
    const updatedStepItems = stepItems.map((item, index) => {
      if (index === selectedIndex) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      } else if (index > selectedIndex) {
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      } else {
        return item;
      }
    });
    setApplicationData(applicationData);
    setStepItems(updatedStepItems);
  };
  const initiatePreviousStep = () => {
    // change current step to pending and previous step to active
    const previousNode = currentStep?.prevNode;
    if (!previousNode) return;

    const previousStepItem = stepItems.find((item) => item.id === previousNode);

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === currentStep?.id) {
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      }
      if (item.id === previousStepItem?.id) {
        return { ...item, status: STEP_STATUS_ACTIVE };
      }
      return item;
    });

    setStepItems(updatedStepItems);
  };
  const initiateKStepPendingClick = async (selectedItem) => {
    // Find the currently active step to compare against later
    const currentActiveStep = stepItems.find(
      (item) => item.status === STEP_STATUS_ACTIVE
    );

    // Get the index of the step the user clicked on
    const selectedIndex = stepItems.findIndex(
      (item) => item.id === selectedItem.id
    );

    // Performance optimization: If the clicked step is already active, skip any updates
    if (currentActiveStep?.id === selectedItem.id) {
      return;
    }

    // Limit validation to steps up to and including the selected step to enforce sequential order
    const stepsToValidate = stepItems.slice(0, selectedIndex + 1);

    // Validate all steps up to the selected one asynchronously, ensuring we wait for all results
    const responses = await Promise.all(
      stepsToValidate.map(async (item) => {
        return await currentStepHasErrors(item); // Returns true if step is valid, false if invalid
      })
    );

    // Find the index of the first invalid step (if any); -1 means all steps are valid
    const failedIndex = responses.findIndex((response) => !response);

    // Determine the target step: move to the selected step if all prior steps are valid,
    // otherwise move to the first invalid step
    const targetIndex = failedIndex === -1 ? selectedIndex : failedIndex;

    // Performance optimization: If the target step is already active, skip the update
    // This prevents redundant re-renders (e.g., clicking Step 4 when Step 2 is active and invalid)
    if (
      currentActiveStep &&
      stepItems[targetIndex].id === currentActiveStep.id
    ) {
      return;
    }

    // Update the step statuses based on the target index
    const updatedStepItems = stepItems.map((item, index) => {
      if (index < targetIndex) {
        // All steps before the target are marked as completed
        return { ...item, status: STEP_STATUS_COMPLETED, enabled: true };
      } else if (index === targetIndex) {
        // The target step becomes active
        return { ...item, status: STEP_STATUS_ACTIVE, enabled: true };
      } else {
        // Steps after the target remain pending and disabled
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      }
    });

    // Apply the updated step statuses to the state
    setStepItems(updatedStepItems);
  };
  const currentStepHasErrors = async (kStep) => {
    const componentRef = childRefs[`${kStep['componentRef']}`];
    const componentModel = await componentRef?.current?.getModel();
    if (componentModel?.hasValidData) {
      setPayload(kStep, componentModel?.payload);
    }
    return componentModel?.hasValidData;
  };
  const setPayload = (kStep = {}, updatedPayload = {}) => {
    const key = kStep['id'];
    payload.current = {
      ...payload.current,
      [key]: updatedPayload,
    };
  };

  /* eslint-disable */
  const skipApproval = useMemo(() => {
    const skipApprovalValue = shouldSkipReapproval({
      sendMode,
      type,
      isApprovalEnabled,
      isReapprovalFieldsModified,
    });
    setSkipApproval(skipApprovalValue);
    return skipApprovalValue;
  }, [
    sendMode,
    type,
    isApprovalEnabled,
    isReapprovalFieldsModified,
    setSkipApproval,
    setIsReapprovalFieldsModified,
  ]);
  /* eslint-enable */

  const initiateAPIContract = async () => {
    setIsCreationInProgress(true);
    setCreateOrUpdateInitiated(true);
    let apiStatus = true;
    const currentStepID = currentStep?.id;
    if (currentStepID === 'offerDetails') {
      apiStatus = await initiateOfferCreationUpdation();
    }
    if (currentStepID === 'offerLetterTemplate') {
      if (sendMode) {
        // In send mode the offer is already created; offerDetails is empty (no Details step),
        // so skipping the PUT avoids a 400 from missing required fields. The template is
        // saved as part of generate_document in DocumentPreview.
        apiStatus = true;
        // SpotDraft has no second (message) pane in this flow — OFFER_STEPS_SEND_SPOTDRAFT
        // is a single step, so the template step doubles as the final send step.
        if (signatureType === 'spotdraft') {
          apiStatus = await initiateOfferEmailSubmission();
          if (apiStatus) {
            // onSuccess closes this drawer; the card button carries the poll.
            await onSuccess?.();
          }
        }
      } else {
        apiStatus = await initiateOfferTemplateUpdation();
        if (skipApproval) {
          onSuccess?.();
        }
      }
    }
    if (currentStepID === 'offerApproval') {
      apiStatus = await initiateOfferApprovalSubmission();
      if (apiStatus) {
        setIsCreationInProgress(false);
        onSuccess?.();
      }
    }
    if (currentStepID === 'offerEmail') {
      apiStatus = await initiateOfferEmailSubmission();
      if (apiStatus) {
        setIsCreationInProgress(false);
        onSuccess?.();
      }
    }

    setIsCreationInProgress(false);
    return apiStatus;
  };

  const initiateOfferCreationUpdation = async (
    triggerReference: string = 'default'
  ) => {
    // Upload any attached files first and swap them for their signed blob ids.
    // Raw dropzone Files otherwise serialize to `[{ path }]`, which the offer
    // API rejects with a 422. Same direct-upload flow used by requisitions /
    // candidate-apply. Persist the result so later steps reuse the signed ids
    // instead of re-uploading.
    let detailValues = payload.current?.offerDetails || {};
    try {
      detailValues = await processFormFilesForUpload(detailValues);
      payload.current.offerDetails = detailValues;
    } catch (uploadError: any) {
      setIsCreationInProgress(false);
      toast({
        title: 'Failed to upload the attached file',
        description:
          uploadError?.response?.data?.errors?.[0] ||
          uploadError?.message ||
          'Please retry.',
        status: 'error',
      });
      return false;
    }

    const missingRequired = getMissingRequiredCustomFields(
      customFieldsData?.ats_form_fields || [],
      detailValues
    );
    if (missingRequired.length > 0) {
      setIsCreationInProgress(false);
      toast({
        title: `${missingRequired[0]} is required`,
        status: 'error',
      });
      return false;
    }

    const offerPayload = generateOfferPayload({
      values: detailValues,
      applicationId: applicationData?.id,
      cfIdToRowIdMap: cfIdToRowIdMapRef.current,
    });

    // Read the latest offerId from the store, not the closed-over selector value: the skip
    // handler is memoized, so its stale closure would otherwise re-create an already-created
    // offer (CORE-4679).
    const offerID = inComingOffer?.id || useOfferStore.getState().offerId;

    const apiToInvoke = shouldCreateOffer({
      type,
      isOfferCreated,
      offerId: offerID,
    })
      ? offerDetailsAPI.createOffer
      : offerDetailsAPI.updateOffer;

    const response = (await apiToInvoke({
      payload: offerPayload,
      id: offerID,
    })) as any;

    const hasErrors =
      Array.isArray(response?.errors) && response.errors.length > 0;

    if (hasErrors) {
      offerCreationErrorHandler(response, triggerReference);
      return false;
    }

    // Success flow
    if (triggerReference === 'draft') {
      toast({
        title: 'Offer draft saved successfully',
        status: 'success',
      });
    }

    if (type === 'create') {
      setIsOfferCreated(true);
    }

    if (type === 'edit' && isApprovalEnabled) {
      const reapprovalRequired = response?.data?.requires_preapproval;
      // When null/undefined, treat as reapproval required since the offer is
      // not complete yet.
      const isModified =
        reapprovalRequired === null ||
        reapprovalRequired === undefined ||
        Boolean(reapprovalRequired);
      setIsReapprovalFieldsModified(isModified);

      // let reapprovalRequired = response?.data?.requires_preapproval;
      // if (isApprovalEnabled && reapprovalRequired == null) {
      //   // it will be null for when offer isn't completed yet
      //   setIsReapprovalFieldsModified(true);
      // } else {
      //   if (isApprovalEnabled && reapprovalRequired) {
      //     setIsReapprovalFieldsModified(true);
      //   } else {
      //     setIsReapprovalFieldsModified(false);
      //   }
      // }
    }

    setOfferId(response?.data?.id);
    // Keep the full offer in the store (carries send_with_*_enabled) so the
    // "Send offer" dropdown can show only connected e-sign integrations.
    setCurrentOffer(response?.data);
    return true;
  };

  const offerCreationErrorHandler = (
    response: any,
    triggerReference: string
  ) => {
    // Fixed title + mapped message as the description — mirrors the
    // requisition toasts; no raw "Error code: err_..." leaks to users.
    const errorMessage = getErrorMessage(response, 'offers');
    toast({
      title: `Failed to ${triggerReference === 'draft' ? 'draft' : 'create'} offer`,
      description: errorMessage,
      status: 'error',
    });
  };

  const initiateOfferTemplateUpdation = async () => {
    const templateData: any = payload.current?.offerLetterTemplate || {};
    const hasOfferDetails =
      Object.keys(payload.current?.offerDetails || {}).length > 0;

    // When offer details were collected in this session, include them so the
    // server gets the full up-to-date payload. When re-entering from the
    // approval flow the details step is skipped, so payload.current.offerDetails
    // is empty — sending generateOfferPayload({}) produces a payload with all
    // required fields null/undefined which triggers a 400. Send only template
    // fields in that case; the existing offer fields are already saved on the server.
    const combinedPayload = hasOfferDetails
      ? (() => {
          const offerPayload = generateOfferPayload({
            values: payload.current.offerDetails,
            applicationId: applicationData?.id,
            cfIdToRowIdMap: cfIdToRowIdMapRef.current,
          });
          return {
            ...offerPayload,
            ats_offer: {
              ...offerPayload?.ats_offer,
              ats_offer_template_id: templateData?.ats_offer_template_id,
              ats_offer_template: templateData?.ats_offer_template,
            },
          };
        })()
      : {
          ats_offer: {
            ats_offer_template_id: templateData?.ats_offer_template_id,
            ats_offer_template: templateData?.ats_offer_template,
          },
        };

    const response = (await offerDetailsAPI.updateOffer({
      payload: combinedPayload,
      id: offerId,
    })) as any;

    if (response?.errors?.length === 0) {
      setCurrentOffer(response?.data);
      return true;
    } else {
      // Fixed title + mapped message as the description — mirrors the
      // requisition toasts; no raw "Error code: err_..." leaks to users.
      const errorMessage = getErrorMessage(response, 'offers');
      toast({
        title: 'Failed to update offer',
        description: errorMessage,
        status: 'error',
      });
    }
    return false;
  };

  const handleTemplateSelect = useCallback(
    async (templateId: string, templateValue: any) => {
      const hasOfferDetails =
        Object.keys(payload.current?.offerDetails || {}).length > 0;

      // In send mode there is no offerDetails step, so fall back to the
      // existing offer's field values to avoid sending null-filled payloads.
      const baseValues = hasOfferDetails
        ? payload.current.offerDetails
        : getInitializeOfferValues({
            currentOffer: inComingOffer,
            contactData: applicationData?.contact,
            rowIdToCfIdMap,
          }).details;

      const offerPayload = generateOfferPayload({
        values: baseValues,
        applicationId: applicationData?.id,
        cfIdToRowIdMap: cfIdToRowIdMapRef.current,
      });

      const combinedPayload = {
        ...offerPayload,
        ats_offer: {
          ...offerPayload?.ats_offer,
          ats_offer_template_id: templateId,
          ats_offer_template: templateValue,
        },
      };

      const response = (await offerDetailsAPI.updateOffer({
        payload: combinedPayload,
        id: offerId,
      })) as any;

      if (response?.errors?.length === 0) {
        // Return the per-offer template copy so TemplatesForm can replace the
        // optimistically-set library template (which lacks per-doc token data).
        return response?.data?.ats_offer_template || true;
      }
      return false;
    },
    [
      offerId,
      applicationData?.id,
      applicationData?.contact,
      inComingOffer,
      rowIdToCfIdMap,
    ]
  );

  const initiateOfferApprovalSubmission = async () => {
    const approvalData: any = payload.current?.offerApproval || {};

    const response = (await offerDetailsAPI.sendForApproval({
      id: offerId,
      payload: {
        note: approvalData?.note || '',
      },
    })) as any;

    const hasErrors =
      Array.isArray(response?.errors) && response.errors.length > 0;

    if (!hasErrors) {
      return true;
    } else {
      const errorMessage = getErrorMessage(response, 'offers');
      toast({
        title: 'Unable to create the offer',
        description: errorMessage || `Failed to send for approval`,
        status: 'error',
      });
    }
    return false;
  };

  const initiateOfferEmailSubmission = async () => {
    // A post-approval reapproval-gating change blocks emailing, so route through approval
    // instead of dead-ending: send for approval, then close.
    if (needsApprovalBeforeSend) {
      const sentForApproval = await initiateOfferApprovalSubmission();
      if (sentForApproval) {
        toast({ title: 'Offer sent for approval', status: 'success' });
      }
      return sentForApproval;
    }
    const emailPayload: any = payload.current?.offerEmail || {};

    if (signatureType === 'kula') {
      const response = (await offerDetailsAPI.sendOfferEmail({
        id: offerId,
        payload: emailPayload,
      })) as any;

      if (response?.errors?.length === 0) {
        return true;
      }
      toast({
        title: 'Failed to send offer, please contact support@kula.ai',
        description: response?.errors?.[0]?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      return false;
    } else {
      const sendMutation =
        signatureType === 'pandadoc'
          ? sendWithPandaDoc
          : signatureType === 'adobe'
            ? sendWithAdobe
            : signatureType === 'spotdraft'
              ? sendWithSpotDraftMutation.mutateAsync
              : sendWithDocuSign;

      const response = await sendMutation({
        id: offerId,
        payload: emailPayload,
      });

      if ('error' in response) {
        toast({
          title: 'Unable to send offer',
          status: 'error',
        });
        return false;
      }

      toast({
        title: 'Offer sent',
        status: 'success',
      });
      return true;
    }
  };

  // "Preview on Docusign" CTA on the offer-email step. The button shows a loader while we validate,
  // create the draft and mint the URL, then runPreview redirects the current tab straight to DocuSign.
  // No drawer cleanup — the page navigates away (and reloads on return). Only reset/toast on error.
  const validateAndInitiatePreviewDocusign = async () => {
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;
    if (needsApprovalBeforeSend) {
      toast({
        title: 'Send this offer for approval before previewing it on DocuSign',
        status: 'warning',
      });
      return;
    }
    setIsCreationInProgress(true);
    try {
      const emailPayload: any = payload.current?.offerEmail || {};
      await runPreview({ id: offerId, payload: emailPayload });
    } catch {
      setIsCreationInProgress(false);
      toast({
        title: 'Unable to open the DocuSign preview, please try again',
        status: 'error',
      });
    }
  };

  // styles
  const styles = getVariantStyles('default');

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const renderHeader = useCallback(() => {
    return (
      <Box position="relative">
        <Box display="flex" alignItems="center" gap="10px">
          <KText size="md-semi-bold" color="secondary-900" lineHeight="30px">
            {getHeaderTitle(type, sendMode, eSignType, getCandidateName())}
          </KText>
        </Box>
        <Box mt="12px">
          <KSteps steps={stepItems as any} onSelect={initiateKStepSelection} />
        </Box>
      </Box>
    );
  }, [getCandidateName, stepItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const getNextButtonLabel = useCallback(() => {
    // A reapproval-gating change was made after approval, so the offer must be
    // re-approved before it can be emailed — the send CTA sends for approval instead.
    if (currentStep?.id === 'offerEmail' && needsApprovalBeforeSend) {
      return 'Send for approval';
    }
    if (currentStep?.id === 'offerEmail' && !isApprovalEnabled) {
      const labelMap = {
        kula: 'Send offer with Kula',
        docusign: 'Send offer with DocuSign',
        pandadoc: 'Send offer with PandaDoc',
        adobe: 'Send offer with Adobe',
        spotdraft: 'Generate offer with SpotDraft',
      };
      return labelMap[signatureType] || 'Send offer';
    }
    return currentStep?.submitButtonLabel || 'Next';
  }, [currentStep?.submitButtonLabel, needsApprovalBeforeSend]); // eslint-disable-line react-hooks/exhaustive-deps

  // A resumed/reverted draft opens in edit mode with no generated document, so it behaves
  // like create for the draft/skip buttons (CORE-4822).
  const isDraftOffer = isDraftLikeOffer((inComingOffer as any)?.offer_status);

  const showDraftButton = useMemo(() => {
    // Draft is a create-only concept; in edit the full save handles it (CORE-4698 /
    // product) — except a resumed draft, which can still be re-saved as a draft.
    if (type !== 'create' && !isDraftOffer) return false;
    return (
      // currentStep?.id === 'offerLetterTemplate' ||
      currentStep?.id === 'offerDetails'
    );
  }, [type, isDraftOffer, currentStep?.id, isDraftInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSkipDocButton = useMemo(
    () =>
      // The re-approval edit flow has no template step, so skip-generation never
      // applies there.
      !isReapprovalEdit &&
      shouldShowSkipDocButton({
        stepId: currentStep?.id,
        isApprovalEnabled,
        skipDocFlagEnabled: tOfferSkipDocGeneration,
        type,
        documentGenerationSkipped: (inComingOffer as any)
          ?.document_generation_skipped,
        isDraft: isDraftOffer,
      }),
    [
      currentStep?.id,
      isApprovalEnabled,
      tOfferSkipDocGeneration,
      type,
      inComingOffer,
      isReapprovalEdit,
      isDraftOffer,
    ]
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const initiateSkipToApproval = useCallback(async () => {
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;

    setIsCreationInProgress(true);
    const created = await initiateOfferCreationUpdation();
    setIsCreationInProgress(false);

    if (!created) return;

    // Read isReapprovalFieldsModified fresh from the store (memoized skipApproval is stale here):
    // when reapproval isn't needed, save and close instead of re-entering the Approval step.
    const skip = shouldSkipReapproval({
      sendMode,
      type,
      isApprovalEnabled,
      isReapprovalFieldsModified:
        useOfferStore.getState().isReapprovalFieldsModified,
    });

    if (skip) {
      onSuccess?.();
      return;
    }

    const updatedStepItems = stepItems.map((item) => {
      if (item.id === 'offerDetails')
        return { ...item, status: STEP_STATUS_COMPLETED };
      if (item.id === 'offerLetterTemplate')
        return { ...item, status: STEP_STATUS_PENDING, enabled: false };
      if (item.id === 'offerApproval')
        return {
          ...item,
          status: STEP_STATUS_ACTIVE,
          enabled: true,
          prevNode: 'offerDetails',
          previousButtonLabel: 'Back to offer details',
        };
      return item;
    });
    setStepItems(updatedStepItems);
  }, [currentStep, stepItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDraft = async () => {
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;
    setCreateOrUpdateInitiated(true);
    setIsDraftInProgress(true);
    await initiateOfferCreationUpdation('draft');
    setIsDraftInProgress(false);
  };

  // currentOffer is only populated once the details step saves, so fall back to the incoming
  // offer (edit flow) — both carry the send_with_*_enabled flags.
  const sendOfferOptions = useMemo(() => {
    const offer = currentOffer?.id ? currentOffer : inComingOffer;
    return connectedSignatureIds(offer as any).map((id) => ({
      id,
      ...SIGNATURE_OPTIONS[id],
    }));
  }, [currentOffer, inComingOffer]);

  const handleSignatureSelect = (signatureId: string) => {
    setSignatureType(signatureId as any);
    validateAndInitiateNextStep();
  };

  // Skip-reapproval edit: the template step is terminal — save the offer and close.
  const initiateTemplateSaveAndClose = async () => {
    const hasValidData = await currentStepHasErrors(currentStep);
    if (!hasValidData) return;
    setIsCreationInProgress(true);
    const apiStatus = await initiateOfferTemplateUpdation();
    setIsCreationInProgress(false);
    if (apiStatus) onSuccess?.();
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const renderFooter = useCallback(() => {
    if (
      sendMode &&
      currentStep?.id === 'offerLetterTemplate' &&
      !hasValidTokens
    ) {
      return null;
    }
    return (
      <Flex
        className="schedule-all-footer-container"
        justifyContent={'space-between'}
        w="100%"
      >
        <Flex>
          {currentStep?.prevNode && (
            <Button
              variant="quaternary"
              onClick={initiatePreviousStep}
              data-test-id="secondary-cta-button"
              p="8px 12px !important"
            >
              <Icon as={ChevronLeftIcon} mr="5px" height="18px" width="18px" />
              {currentStep?.previousButtonLabel || 'Back'}
            </Button>
          )}
        </Flex>
        <Flex gap="6px">
          {/* With the multi-doc flag on, the side panel owns "Add more files", so the footer
              button just flashes before the panel mounts — restrict it to the flag-off flow. */}
          {currentStep?.id === 'offerLetterTemplate' &&
            !tOfferTemplateMultiDocuments &&
            !isMultiDocTemplate && (
              <AddMoreFilesButton offerId={inComingOffer?.id || offerId} />
            )}
          {currentStep?.id === 'offerLetterTemplate' &&
          (!isApprovalEnabled || skipApproval) ? (
            sendMode ? (
              <KButton
                variant="primary"
                isLoading={isCreationInProgress}
                isDisabled={setButtonDisabled}
                data-test-id="send-offer-button"
                onClick={validateAndInitiateNextStep}
              >
                {currentStep?.submitButtonLabel || 'Send offer'}
              </KButton>
            ) : skipApproval ? (
              <KButton
                variant="primary"
                isLoading={isCreationInProgress}
                isDisabled={setButtonDisabled}
                data-test-id="save-offer-button"
                onClick={initiateTemplateSaveAndClose}
              >
                Save
              </KButton>
            ) : (
              <KMenuButton
                variant="primary"
                isLoading={isCreationInProgress}
                isDisabled={setButtonDisabled}
                data-test-id="send-offer-button"
                dividerProps={{ display: 'none' }}
                triggerContent={
                  <Flex alignItems="center" gap="8px">
                    <Icon as={Mail02} />
                    Send offer
                  </Flex>
                }
                menuContent={
                  <>
                    {sendOfferOptions.map((option) => (
                      <MenuItem
                        key={option.id}
                        icon={<Icon as={option.icon} boxSize="20px" />}
                        onClick={() => handleSignatureSelect(option.id)}
                        data-test-id={`send-with-${option.id}-button`}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </>
                }
              />
            )
          ) : (
            <Box display="flex" gap="6px">
              {showDraftButton && (
                <KButton
                  variant="secondary"
                  onClick={handleSaveDraft}
                  isLoading={isDraftInProgress}
                  data-test-id="offer-save-draft-button"
                >
                  Save draft
                </KButton>
              )}
              {showSkipDocButton && (
                <KButton
                  variant="tertiary"
                  onClick={initiateSkipToApproval}
                  isLoading={isCreationInProgress}
                  data-test-id="skip-offer-letter-button"
                >
                  Skip offer generation
                </KButton>
              )}
              {currentStep?.id === 'offerEmail' &&
              signatureType === 'docusign' &&
              feDocusignOfferPreview ? (
                <KButton
                  variant="primary"
                  onClick={validateAndInitiatePreviewDocusign}
                  isLoading={isCreationInProgress || isPreviewRunning}
                  isDisabled={setButtonDisabled}
                  data-test-id="preview-on-docusign-button"
                >
                  Preview on Docusign
                </KButton>
              ) : (
                <KButton
                  variant="primary"
                  onClick={validateAndInitiateNextStep}
                  isLoading={isCreationInProgress}
                  isDisabled={setButtonDisabled}
                  data-test-id="primary-cta-button"
                >
                  {getNextButtonLabel()}
                </KButton>
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    );
    // eslint-disable-next-line
  }, [
    currentStep,
    isMultiDocTemplate,
    inComingOffer?.id,
    offerId,
    isCreationInProgress,
    isDraftInProgress,
    setButtonDisabled,
    signatureType,
    feDocusignOfferPreview,
    isPreviewRunning,
    showSkipDocButton,
    initiateSkipToApproval,
    hasValidTokens,
    sendMode,
    isApprovalEnabled,
    skipApproval,
    sendOfferOptions,
    tOfferTemplateMultiDocuments,
  ]);

  // Block the X button for SpotDraft's whole send+poll span, same as the
  // confirm modal's isConfirming guard — otherwise it closes mid-poll.
  const isSpotDraftSendBlocking =
    sendMode && signatureType === 'spotdraft' && isCreationInProgress;

  return (
    <KDrawer
      contentProps={{
        maxW: tOfferTemplateMultiDocuments ? '1000px' : '760px',
        borderTopLeftRadius: 'xl',
      }}
      isOpen={true}
      showCloseButton={!isSpotDraftSendBlocking}
      onClose={() => {
        if (isSpotDraftSendBlocking) return;
        if (createOrUpdateInitiated) {
          onSuccess();
        }
        onClose();
      }}
      handleOutsideClose={() => {}}
      header={<></>}
      footer={<></>}
    >
      {getApprovalInProgress ? (
        <Flex justifyContent="center" alignItems="center" h="100%">
          <Spinner />
        </Flex>
      ) : (
        <Flex
          flexDirection="column"
          position="relative"
          h="100vh"
          className="offer-container"
          data-test-id="offer-container"
        >
          <DrawerHeader {...styles.header} p="18px" flexGrow={0} flexShrink={0}>
            {renderHeader()}
          </DrawerHeader>
          <Box
            p="18px"
            className="offer-content-container"
            data-test-id="offer-content-container"
            flexGrow={1}
            overflow="hidden"
          >
            <Box display="block" overflow="auto" h="100%">
              <OfferBodyContainer
                currentStep={currentStep}
                childRefs={childRefs}
                onTemplateSelect={handleTemplateSelect}
                type={type}
                sendMode={sendMode}
                onEditOffer={onEditOffer}
                reapprovalHighlightTokens={reapprovalHighlightTokens}
              />
            </Box>
          </Box>
          <DrawerFooter {...styles.footer} p="18px" flexGrow={0} flexShrink={0}>
            {renderFooter()}
          </DrawerFooter>
        </Flex>
      )}
    </KDrawer>
  );
};

export default OfferContainer;
